import { createFileRoute } from '@tanstack/react-router'
import { ArrowRightLeft, Plus, Trash2, History } from 'lucide-react'
import { useState, useEffect } from 'react'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useAccounts, useConfirmDialog, useCurrencyFormatter } from '@/hooks'
import {
    useTransfers,
    useCreateTransfer,
    useDeleteTransfer,
    useExchangeRateHistory,
    type TransferListParams,
    type CreateTransferInput,
} from '@/hooks'

export const Route = createFileRoute('/_authenticated/finance/transfers')({
    component: TransfersPage,
})

function TransfersPage() {
    const [filters, setFilters] = useState<TransferListParams>({ page: 1, pageSize: 20 })
    const [showForm, setShowForm] = useState(false)

    const { data, isLoading, error } = useTransfers(filters)
    const { data: accounts } = useAccounts()
    const deleteMutation = useDeleteTransfer()
    const { formatMoney } = useCurrencyFormatter()
    const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

    const handlePageChange = (page: number) => {
        setFilters((prev) => ({ ...prev, page }))
    }

    const handleDelete = async (id: string) => {
        const confirmed = await confirmWithPreset(
            'delete',
            '确定要删除此换汇记录吗？关联的现金流也会被删除。'
        )
        if (confirmed) {
            setLoading(true)
            try {
                await deleteMutation.mutateAsync(id)
            } finally {
                setLoading(false)
            }
        }
    }

    if (error) {
        return (
            <div className="flex h-64 items-center justify-center">
                <div className="text-destructive">加载失败: {error.message}</div>
            </div>
        )
    }

    const transfers = data?.data ?? []
    const meta = data?.meta

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">换汇转账</h1>
                    <p className="text-muted-foreground">跨币种账户转账记录</p>
                </div>
                <button
                    onClick={() => setShowForm(true)}
                    className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                    <Plus className="h-4 w-4" />
                    新增换汇
                </button>
            </div>

            {/* 换汇表单 */}
            {showForm && (
                <TransferForm
                    accounts={accounts ?? []}
                    onSuccess={() => setShowForm(false)}
                    onCancel={() => setShowForm(false)}
                />
            )}

            {/* 换汇列表 */}
            {isLoading ? (
                <div className="flex h-64 items-center justify-center">
                    <div className="text-muted-foreground">加载中...</div>
                </div>
            ) : transfers.length === 0 ? (
                <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
                    <ArrowRightLeft className="h-12 w-12 text-muted-foreground" />
                    <div className="text-center">
                        <p className="text-lg font-medium">暂无换汇记录</p>
                        <p className="text-sm text-muted-foreground">点击上方按钮创建换汇转账</p>
                    </div>
                </div>
            ) : (
                <div className="rounded-lg border">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b bg-muted/50">
                                <th className="px-4 py-3 text-left text-sm font-medium">日期</th>
                                <th className="px-4 py-3 text-left text-sm font-medium">转出账户</th>
                                <th className="px-4 py-3 text-right text-sm font-medium">转出金额</th>
                                <th className="px-4 py-3 text-center text-sm font-medium">汇率</th>
                                <th className="px-4 py-3 text-left text-sm font-medium">转入账户</th>
                                <th className="px-4 py-3 text-right text-sm font-medium">转入金额</th>
                                <th className="px-4 py-3 text-left text-sm font-medium">描述</th>
                                <th className="px-4 py-3 text-right text-sm font-medium">操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transfers.map((transfer) => (
                                <tr key={transfer.id} className="border-b last:border-0 hover:bg-muted/30">
                                    <td className="px-4 py-3 text-sm">{transfer.transferDate}</td>
                                    <td className="px-4 py-3">
                                        <div>
                                            <p className="font-medium">{transfer.fromAccount?.name ?? '-'}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {transfer.fromCurrency}
                                            </p>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-destructive">
                                        -{formatMoney(transfer.fromAmount, transfer.fromCurrency)}
                                    </td>
                                    <td className="px-4 py-3 text-center font-mono text-sm">
                                        {transfer.exchangeRate.toFixed(4)}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div>
                                            <p className="font-medium">{transfer.toAccount?.name ?? '-'}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {transfer.toCurrency}
                                            </p>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-green-600">
                                        +{formatMoney(transfer.toAmount, transfer.toCurrency)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-muted-foreground">
                                        {transfer.description ?? '-'}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            onClick={() => void handleDelete(transfer.id)}
                                            disabled={deleteMutation.isPending}
                                            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                            title="删除"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* 分页 */}
            {meta && meta.totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                        共 {meta.total} 条记录，第 {meta.page}/{meta.totalPages} 页
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handlePageChange(meta.page - 1)}
                            disabled={meta.page <= 1}
                            className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
                        >
                            上一页
                        </button>
                        <button
                            onClick={() => handlePageChange(meta.page + 1)}
                            disabled={meta.page >= meta.totalPages}
                            className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
                        >
                            下一页
                        </button>
                    </div>
                </div>
            )}

            <ConfirmDialog />
        </div>
    )
}

// ==================== 换汇表单组件 ====================

interface TransferFormProps {
    accounts: Array<{ id: string; name: string; currency: string }>
    onSuccess: () => void
    onCancel: () => void
}

function TransferForm({ accounts, onSuccess, onCancel }: TransferFormProps) {
    const createMutation = useCreateTransfer()
    const [formData, setFormData] = useState({
        fromAccountId: '',
        toAccountId: '',
        fromAmount: '',
        toAmount: '',
        exchangeRate: '',
        transferDate: new Date().toISOString().split('T')[0],
        description: '',
    })

    const fromAccount = accounts.find((a) => a.id === formData.fromAccountId)
    const toAccount = accounts.find((a) => a.id === formData.toAccountId)

    // 获取历史汇率
    const { data: rateHistory } = useExchangeRateHistory(
        fromAccount?.currency ?? '',
        toAccount?.currency ?? '',
        5
    )

    // 当选择账户后，自动填充最近汇率
    useEffect(() => {
        if (rateHistory?.latestRate && !formData.exchangeRate) {
            setFormData((prev) => ({
                ...prev,
                exchangeRate: rateHistory.latestRate?.toFixed(6) ?? '',
            }))
        }
    }, [rateHistory?.latestRate, formData.exchangeRate])

    // 自动计算汇率或转入金额
    const handleFromAmountChange = (value: string) => {
        setFormData((prev) => {
            const fromAmount = parseFloat(value) || 0
            const rate = parseFloat(prev.exchangeRate) || 0
            const toAmount = rate > 0 ? (fromAmount * rate).toFixed(2) : prev.toAmount
            return { ...prev, fromAmount: value, toAmount }
        })
    }

    const handleExchangeRateChange = (value: string) => {
        setFormData((prev) => {
            const rate = parseFloat(value) || 0
            const fromAmount = parseFloat(prev.fromAmount) || 0
            const toAmount =
                rate > 0 && fromAmount > 0 ? (fromAmount * rate).toFixed(2) : prev.toAmount
            return { ...prev, exchangeRate: value, toAmount }
        })
    }

    const handleToAmountChange = (value: string) => {
        setFormData((prev) => {
            const toAmount = parseFloat(value) || 0
            const fromAmount = parseFloat(prev.fromAmount) || 0
            const rate = fromAmount > 0 ? (toAmount / fromAmount).toFixed(6) : prev.exchangeRate
            return { ...prev, toAmount: value, exchangeRate: rate }
        })
    }

    const applyHistoryRate = (rate: number) => {
        setFormData((prev) => {
            const fromAmount = parseFloat(prev.fromAmount) || 0
            const toAmount = fromAmount > 0 ? (fromAmount * rate).toFixed(2) : prev.toAmount
            return { ...prev, exchangeRate: rate.toFixed(6), toAmount }
        })
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        const input: CreateTransferInput = {
            fromAccountId: formData.fromAccountId,
            toAccountId: formData.toAccountId,
            fromAmount: Math.round(parseFloat(formData.fromAmount) * 100),
            toAmount: Math.round(parseFloat(formData.toAmount) * 100),
            exchangeRate: parseFloat(formData.exchangeRate),
            transferDate: formData.transferDate,
            description: formData.description || undefined,
        }
        createMutation.mutate(input, { onSuccess })
    }

    return (
        <div className="rounded-lg border p-4">
            <h3 className="mb-4 font-semibold">新增换汇转账</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                    <div>
                        <label className="mb-1 block text-sm font-medium">转出账户</label>
                        <Select
                            value={formData.fromAccountId || 'placeholder'}
                            onValueChange={(value) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    fromAccountId: value === 'placeholder' ? '' : value,
                                    exchangeRate: '', // 重置汇率
                                }))
                            }
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="选择账户" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="placeholder" disabled>选择账户</SelectItem>
                                {accounts.map((account) => (
                                    <SelectItem key={account.id} value={account.id}>
                                        {account.name} ({account.currency})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium">
                            转出金额 {fromAccount && `(${fromAccount.currency})`}
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.fromAmount}
                            onChange={(e) => handleFromAmountChange(e.target.value)}
                            className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            placeholder="0.00"
                            required
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium">汇率</label>
                        <input
                            type="number"
                            step="0.000001"
                            min="0"
                            value={formData.exchangeRate}
                            onChange={(e) => handleExchangeRateChange(e.target.value)}
                            className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            placeholder="1.000000"
                            required
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium">转入账户</label>
                        <Select
                            value={formData.toAccountId || 'placeholder'}
                            onValueChange={(value) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    toAccountId: value === 'placeholder' ? '' : value,
                                    exchangeRate: '', // 重置汇率
                                }))
                            }
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="选择账户" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="placeholder" disabled>选择账户</SelectItem>
                                {accounts
                                    .filter((a) => a.id !== formData.fromAccountId)
                                    .map((account) => (
                                        <SelectItem key={account.id} value={account.id}>
                                            {account.name} ({account.currency})
                                        </SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium">
                            转入金额 {toAccount && `(${toAccount.currency})`}
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.toAmount}
                            onChange={(e) => handleToAmountChange(e.target.value)}
                            className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            placeholder="0.00"
                            required
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium">转账日期</label>
                        <input
                            type="date"
                            value={formData.transferDate}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, transferDate: e.target.value }))
                            }
                            className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            required
                        />
                    </div>
                </div>

                {/* 历史汇率参考 */}
                {rateHistory && rateHistory.history.length > 0 && (
                    <div className="rounded-md bg-muted/50 p-3">
                        <div className="mb-2 flex items-center gap-2 text-sm font-medium text-muted-foreground">
                            <History className="h-4 w-4" />
                            历史汇率参考 ({fromAccount?.currency} → {toAccount?.currency})
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {rateHistory.history.map((h, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => applyHistoryRate(h.exchangeRate)}
                                    className="rounded border bg-background px-2 py-1 text-xs font-mono hover:bg-muted"
                                    title={`${h.transferDate}`}
                                >
                                    {h.exchangeRate.toFixed(4)}
                                    <span className="ml-1 text-muted-foreground">
                                        ({h.transferDate.slice(5)})
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="grid gap-4 md:grid-cols-3">
                    <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium">描述（可选）</label>
                        <input
                            type="text"
                            value={formData.description}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, description: e.target.value }))
                            }
                            className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            placeholder="换汇原因"
                        />
                    </div>
                    <div className="flex items-end gap-2">
                        <button
                            type="submit"
                            disabled={createMutation.isPending}
                            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                        >
                            {createMutation.isPending ? '提交中...' : '提交'}
                        </button>
                        <button
                            type="button"
                            onClick={onCancel}
                            className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
                        >
                            取消
                        </button>
                    </div>
                </div>
            </form>
            {createMutation.error && (
                <p className="mt-2 text-sm text-destructive">{createMutation.error.message}</p>
            )}
        </div>
    )
}
